'use client';

import { useQuery } from '@tanstack/react-query';
import { isEqual } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useConfig } from 'wagmi';
import type { AssetItem } from '@/components/common/TableAdvancedFilters/AssetFilter';
import { useWeb3Context } from '@/contexts/Web3Context';
import { fetchUserHoldings } from '@/helpers/filter/fetchUserHoldings';
import { logger } from '@/lib/observability/logger';
import { type ChainHoldingsResult, normalizeHoldingsTokenIds, partitionChainHoldings } from './useInWalletFilter.helpers';
interface UseInWalletFilterProps {
  assets: AssetItem[];
  currentAssets: string[];
  onChange: (assets: string[]) => void;
}

/**
 * Groups assets by chain ID for parallel per-chain fetching.
 * Assets without explicit chain info fall back to `fallbackChainId`.
 */
function groupAssetsByChain(assets: AssetItem[]): Map<number, AssetItem[]> {
  const map = new Map<number, AssetItem[]>();
  for (const asset of assets) {
    const chainId = asset.chain.id;
    const existing = map.get(chainId);
    if (existing) {
      existing.push(asset);
    } else {
      map.set(chainId, [asset]);
    }
  }
  return map;
}

/**
 * Hook that manages the "In Wallet" filter.
 * Fetches user holdings across all chains represented in the asset list.
 */
export const useInWalletFilter = ({
  assets,
  currentAssets,
  onChange
}: UseInWalletFilterProps) => {
  const {
    account
  } = useWeb3Context();
  const config = useConfig();
  const [isActive, setIsActive] = useState(false);
  const isTriggerByOwn = useRef(false);
  const toggle = (_isActive: boolean) => {
    isTriggerByOwn.current = true;
    setIsActive(_isActive);
  };
  const reset = () => {
    isTriggerByOwn.current = false;
    setIsActive(false);
  };
  const assetsByChain = groupAssetsByChain(assets);
  const assetIds = assets.map(a => a.id).toSorted();
  const {
    data: holdings,
    isLoading,
    isError
  } = useQuery({
    queryKey: ['userHoldings', account?.address, assetIds],
    queryFn: async () => {
      // Per-chain holdings fetches must degrade gracefully: a single chain's
      // RPC outage (e.g. Stable/988 returning -32000) must not blank the whole
      // markets table. Each chain is fetched independently and its outcome
      // tagged with the chain id; failed chains are logged and dropped, and a
      // partial outage with surviving holdings keeps working. The query only
      // errors when the result is indeterminate (see the throw below). See VAU-1227.
      const chainEntries = Array.from(assetsByChain.entries());
      const results = await Promise.all(chainEntries.map(async ([chainId, chainAssets]): Promise<ChainHoldingsResult> => {
        try {
          const client = config.getClient({
            chainId
          });
          const holdings = await fetchUserHoldings({
            client,
            assets: chainAssets,
            userAddress: account!.address!,
            targetChainId: chainId
          });
          return {
            chainId,
            status: 'fulfilled',
            holdings
          };
        } catch (reason) {
          return {
            chainId,
            status: 'rejected',
            reason
          };
        }
      }));
      const {
        holdings: chainHoldings,
        failures,
        isIndeterminate
      } = partitionChainHoldings(results);

      // Tolerated per-chain failures log at `warn` (BetterStack only). Logging
      // at `error` with an Error attached would push every blip to Sentry via
      // the reporter gate, regardless of level (see client-logger.ts).
      for (const {
        chainId,
        reason
      } of failures) {
        logger.warn('In-wallet filter: holdings fetch failed for chain', {
          chainId,
          walletAddress: account!.address!,
          extra: {
            error: reason instanceof Error ? reason.message : String(reason)
          }
        });
      }

      // A chain failed and the ones that responded hold nothing: we can't tell
      // "no holdings" from "holdings only on the failed chain", so surface an
      // error/unknown instead of a misleading empty state. Covers a total
      // outage too. A partial outage WITH surviving holdings degrades silently.
      if (isIndeterminate) {
        throw new Error(`In-wallet filter: holdings indeterminate (${failures.length} of ${chainEntries.length} chain(s) failed)`);
      }
      return chainHoldings;
    },
    // Successful holdings are cached per query key and never passively
    // refetched. The applied Consumer-API filter is re-synced from `onChange`
    // only on a user toggle, so a background refetch would refresh holdings
    // without updating the filter, and the two would silently diverge. A
    // transient outage therefore recovers on reload, not automatically. A
    // proper in-session self-heal would need the onChange/isActive propagation
    // reworked; that is a tracked follow-up, deliberately out of scope here.
    staleTime: Infinity,
    // Only an indeterminate result throws; the default 3 retries would just
    // multiply the per-chain logs and delay the error surfacing. One is enough.
    retry: 1,
    // An inactive filter never STARTS a holdings fetch. A fetch already in
    // flight when the filter is toggled off still completes (and may warn-log a
    // failed chain); we accept that stray completion rather than thread an
    // abort signal through fetchUserHoldings/viem for a benign late log.
    enabled: isActive && !!account?.address && assets.length > 0
  });

  // useMemo is critical here to maintain referential stability.
  // Without it, a new array would be created on every render
  const holdingsNormalized = useMemo(() => {
    return normalizeHoldingsTokenIds(holdings);
  }, [holdings]);

  // Actualize isActive when only currentAssets change
  useEffect(() => {
    if (isActive && holdingsNormalized) {
      const isEqualAssets = isEqual(new Set(currentAssets.map(v => v.toLowerCase())), new Set(holdingsNormalized.map(v => v.toLowerCase())));
      setIsActive(isEqualAssets);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run when currentAssets change
  }, [currentAssets]);
  useEffect(() => {
    if (isTriggerByOwn.current && holdingsNormalized) {
      isTriggerByOwn.current = false;
      onChange(isActive ? holdingsNormalized : []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run when holdings change or isActive changes
  }, [isActive, holdingsNormalized]);
  return {
    toggle,
    reset,
    isLoading,
    isActive,
    isError,
    holdingsSelected: isActive && holdingsNormalized ? holdingsNormalized : []
  };
};